<script>
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { dynamicTime } from 'helpers/timeHelper';
import FileUpload from 'vue-upload-component';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import { useAlert, useTrack } from 'dashboard/composables';
import { ref, computed, getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';
import AttachmentPreview from 'components/widgets/AttachmentsPreview.vue';
import { ALLOWED_FILE_TYPES_FOR_LINE } from 'shared/constants/messages';
import { required, numeric, minValue, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import router from '../../routes';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import WootSubmitButton from '../../components/buttons/FormSubmitButton.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
//import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper.js';
import numeral from 'numeral';

export default {
  name: 'OrderForm',
  components: {
    WootSubmitButton,
    AttachmentPreview,
    Input,
    ComboBox,
    Avatar,
    FileUpload,
    Spinner,
  },
  mixins: [fileUploadMixin],
  props: {
    contact: {
      type: Object,
      default: null,
    },
    currentChat: {
      type: Object,
      default: null,
    },
  },
  emits: ['created'],
  setup() {
    const uploadRef = ref(false);
    // useEmitter('messageForOrder', message => {
    //   //this.initOrderWithMessage(message);
    //   const instance = getCurrentInstance();
    //   instance.ctx.initOrderWithMessage(message);
    // });
    return {
      uploadRef,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      formData: {
        country: '',
        cardType: '',
        cardFaceValue: '',
        cardRate: '',
        code: '',
        images: [],
        remark: '',
      },
      cardTypeOptions: [
        { value: 'visa', label: 'Visa' },
        { value: 'mc', label: 'MasterCard' },
        { value: 'amex', label: 'American Express' },
        { value: 'discover', label: 'Discover' },
      ],
      attachedFiles: [],
      member: [],
      addOrderRule: { showLoading: false, message: '' },
      messageIds: [],
      messageOrder: false,
    };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      currentUser: 'getCurrentUser',
      countries: 'giftCards/getCountries',
      rateList: 'giftCards/getRateList',
      memberDetails: 'giftCards/getMemberDetails',
      uiFlags: 'giftCards/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    createdAt() {
      return this.contact?.createdAt ? dynamicTime(this.contact.createdAt) : '';
    },
    lastActivityAt() {
      return this.contact?.lastActivityAt
        ? dynamicTime(this.contact.lastActivityAt)
        : '';
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES_FOR_LINE;
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    rateListOptions() {
      return this.rateList.map(rate => ({
        value: String(rate.id),
        label: `${rate.category} / ${rate.currency} / ${rate.name}`,
      }));
    },
    countriesOptions() {
      return this.countries.map(country => ({
        value: String(country.id),
        label: country.name,
      }));
    },
    isCountrySelectorDisabled() {
      return Boolean(this.memberDetails?.user_info?.country_id);
    },
    currentCountryObject() {
      return this.countries.find(
        country => Number(country.id) === Number(this.formData.country)
      );
    },
    // 根据选择的 cardRate 动态获取 rateList 中的完整对象
    currentRateObject() {
      return this.rateList.find(
        rate => Number(rate.id) === Number(this.formData.cardType)
      );
    },
    currentRate() {
      return this.currentRateObject ? this.currentRateObject.rate : null;
    },
    computedCashAmount() {
      const selectedRate = this.formData.cardRate;

      if (selectedRate) {
        const rate = selectedRate || 0; // 如果 rate 属性不存在，默认使用 0
        const faceValue = Number(this.formData.cardFaceValue) || 0; // 确保 cardFaceValue 是数字
        const result = faceValue * rate;
        return numeral(result).format(`0,0.00`);
      }

      return 0;
    },
    image_ids() {
      return this.attachedFiles
        .filter(file => file.blobSignedId !== null)
        .map(file => file.blobSignedId);
    },
    isFetching() {
      return (
        this.uiFlags.fetchingCountries ||
        this.uiFlags.fetchingRateList ||
        this.uiFlags.fetchingMemberDetails
      );
    },
    disabledCode() {
      return this.messageOrder;
    },
    encryptMessage() {
      return hasPermissions(['encrypt_message'], this.userPermissions);
    },
  },
  mounted() {
    this.initComponent();
    emitter.on('messageForOrder', (message) => {
      this.initOrderWithMessage(message); // 这里的 this 指向 Vue 组件实例
    });
  },
  beforeUnmount(){
    console.log('beforeUnmount');
    emitter.off('messageForOrder');
  },
  validations() {
    let validations = {
      formData: {
        country: {
          required, // 必填字段
        },
        cardType: {
          required, // 必填字段
        },
        cardFaceValue: {
          required, // 必填字段
          numeric, // 必须是数字
          minValue: minValue(5),
        },
        cardRate: {
          required, // 必填字段
          numeric, // 必须是数字
          minValue: minValue(0.1),
        },
      },
    };
    return validations;
  },
  methods: {
    initComponent() {
      this.fetchMemberDetails();
      this.fetchCountries();
      if (this.formData.country) {
        this.fetchRateList();
      }
      const defaultCountry = this.countriesOptions.find(
        option =>
          option.label === String(this.contact?.additional_attributes?.country)
      );
      if (defaultCountry) {
        this.formData.country = defaultCountry.value;
      }
    },
    async fetchMemberDetails() {
      this.$store
        .dispatch('giftCards/getMemberDetails', {
          contact_id: this.contact.id,
        })
        .catch(error => {
          const errorMessage = error?.message || 'error';
          this.useAlert(errorMessage);
        });
    },
    async fetchCountries() {
      // this.$store.dispatch('giftCards/getCountries').catch(error => {
      //   const errorMessage = error?.message || 'error';
      //   this.useAlert(errorMessage);
      // });
    },
    async fetchRateList() {
      this.$store
        .dispatch('giftCards/getRateList', {
          country_id: this.formData.country,
          conversation_id: this.currentChat.id,
        })
        .catch(error => {
          const errorMessage = error?.message || 'error';
          this.useAlert(errorMessage);
        });
    },
    onFileUpload(file) {
      this.onDirectFileUpload(file);
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: 0,
          resource: blob || file,
          isPrivate: false,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
      const newMessageIds = this.attachedFiles
        .filter(item => item.message_id !== null)
        .map(item => item.message_id);
      this.messageIds = newMessageIds;
    },
    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.formData.images = this.image_ids;
      if (this.messageIds.length === 0) {
        if (this.formData.images.length === 0 && this.formData.code === '') {
          useAlert('You must input a code or upload a gift card image.');
          return;
        }
      }

      this.addOrderRule.showLoading = true;
      try {
        const orderResult = await this.$store.dispatch(
          'giftCards/createOrder',
          {
            country_id: this.formData.country,
            card_type: this.formData.cardType,
            rate: this.formData.cardRate,
            face_value: this.formData.cardFaceValue,
            code: this.formData.code,
            image_ids: this.image_ids,
            conversation_id: this.currentChat.id,
            message_ids: this.messageIds,
            remark: this.formData.remark,
          }
        );
        this.resetForm();
        useAlert('Create order success');
        this.$emit('created', orderResult);
      } catch (error) {
        const errorMessage =
          parseAPIErrorResponse(error) || 'Create order error.';
        useAlert(errorMessage);
      } finally {
        this.addOrderRule.showLoading = false;
      }
    },
    resetForm() {
      this.formData.cardType = '';
      this.formData.cardRate = '';
      this.formData.cardFaceValue = '';
      this.formData.code = '';
      this.formData.remark = '';
      this.attachedFiles = [];
      this.messageIds = [];
      this.v$.formData.cardType.$reset();
      this.v$.formData.cardRate.$reset();
      this.v$.formData.cardFaceValue.$reset();
    },
    initOrderWithMessage(message) {
      console.log('initOrderWithMessage message log create order: ', message);
      if (!this.messageIds.includes(message.id)) {
        this.messageIds.push(message.id);

        if (message.attachments && message.attachments.length > 0) {
          const file = message.attachments[0];
          this.attachedFiles.push({
            currentChatId: 0,
            resource: {
              content_type: 'image',
              filename: '',
              byte_size: file.file_size,
            },
            isPrivate: false,
            thumb: file.data_url,
            blobSignedId: null,
            isRecordedAudio: false,
            message_id: message.id,
          });
        } else if (message.content_attributes?.gift_card_info?.cdkey) {
          this.messageOrder = true;
          this.formData.code = this.encryptMessage
            ? message.content
            : message.content_attributes?.gift_card_info?.cdkey;
        }
      }
    },
  },
  watch: {
    memberDetails: {
      immediate: true,
      handler(newVal) {
        if (newVal?.user_info?.country_id) {
          this.formData.country = String(newVal.user_info.country_id);
        }
      },
    },
    'formData.country': {
      immediate: false, // 初次加载时不调用，只监听后续交互
      handler(newCountryId) {
        // 当用户重新选择国家时，调用 fetchRateList
        if (newCountryId) {
          this.fetchRateList(newCountryId);
        }
      },
    },
    'formData.cardType': {
      immediate: false, // 初次加载时不调用，只监听后续交互
      handler(newCardTypeId) {
        if (newCardTypeId) {
          this.formData.cardRate = this.currentRate;
        }
      },
    },
    contact: {
      immediate: true,
      deep: true, // 如果 `contact` 是对象，确保只监听它的属性变化
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.id !== oldVal.id) {
          this.initComponent(); // 仅在 ID 变化时调用
        }
      },
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 bg-gray-100 space-y-6">
    <!--    <div class="flex items-center gap-3">-->
    <!--      &lt;!&ndash; Avatar 部分 &ndash;&gt;-->
    <!--      <Avatar-->
    <!--          :src="avatarSrc || ''"-->
    <!--          :name="contact?.name || ''"-->
    <!--          :size="42"-->
    <!--      />-->

    <!--      &lt;!&ndash; 文本部分 &ndash;&gt;-->
    <!--      <div class="flex flex-col gap-1">-->
    <!--        <h3 class="text-base font-medium text-n-slate-12">-->
    <!--          {{ contact?.name }}-->
    <!--        </h3>-->
    <!--      </div>-->
    <!--    </div>-->
    <!-- 表单部分 -->
    <form class="flex flex-col gap-4" autocomplete="off" @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          Country
        </label>
        <ComboBox
          id="country"
          v-model="formData.country"
          :options="countriesOptions"
          :disabled="isFetching || isCountrySelectorDisabled"
          :placeholder="'Selected country'"
          :label="'Country'"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          :message="v$.formData.country.$errors[0]?.$message"
          :hasError="v$.formData.country.$error"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          Card type
        </label>
        <ComboBox
          id="cardType"
          v-model="formData.cardType"
          :options="rateListOptions"
          :disabled="isFetching"
          placeholder="Selected card type"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          :message="v$.formData.cardType.$errors[0]?.$message"
          :has-error="v$.formData.cardType.$error"
        />
      </div>

      <Input
        v-model="formData.cardRate"
        :id="'cardRate'"
        type="number"
        :placeholder="''"
        :label="'Card rate'"
        :message="v$.formData.cardRate.$errors[0]?.$message"
        :messageType="v$.formData.cardRate.$error ? 'error' : 'info'"
      />

      <Input
        type="number"
        v-model="formData.cardFaceValue"
        :id="'number'"
        :placeholder="'Please enter the face value'"
        :label="'Face value'"
        :message="v$.formData.cardFaceValue.$errors[0]?.$message"
        :messageType="v$.formData.cardFaceValue.$error ? 'error' : 'info'"
      />

      <Input
        type="input"
        autocomplete="off"
        v-model="formData.code"
        :disabled="disabledCode"
        :id="'code'"
        :placeholder="'Please enter the gift card code'"
        :label="'Code'"
      />

      <Input
        type="input"
        v-model="formData.remark"
        :id="'remarks'"
        :placeholder="'remarks'"
        :label="'Remarks'"
      />

      <FileUpload
        ref="uploadRef"
        v-tooltip.top-end="'upload images'"
        input-id="orderAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="true"
        :drop="true"
        :drop-directory="false"
        :class="'file-content'"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <div class="upload-content" @click="">
          <p class="upload-text">Drag and drop,or <a>click to upload</a></p>
        </div>
      </FileUpload>

      <div v-if="hasAttachments" class="attachment-preview-box">
        <AttachmentPreview
          class="flex-col mt-4"
          :attachments="attachedFiles"
          @remove-attachment="removeAttachment"
        />
      </div>

      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex top-0 bottom-0 left-0 right-0 z-20 flex flex-col items-center justify-center w-full h-full gap-2 text-slate-900 dark:text-slate-50 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-slate-900 dark:text-slate-50">
            Drop Gift Card Images
          </h4>
        </div>
      </transition>

      <div v-if="computedCashAmount" class="cash-amount-container">
        <p class="cash-amount-text">
          Order Amount:
          <span class="cash-amount-value"
            >
            {{ currentCountryObject?.currency_symbol }}
            {{ computedCashAmount }}</span
          >
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <WootSubmitButton
          :disabled="addOrderRule.showLoading"
          :buttonText="'Submit'"
          :loading="addOrderRule.showLoading"
          :buttonClass="'center-text'"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
/* 可选样式调整 */
.upload-content {
  text-align: center;
  color: #666;
  transition:
    background-color 0.3s,
    border 0.3s;
}

.upload-text {
  font-size: 14px;
  color: #777;
}

.file-content:hover {
  //background-color: #f0f0f0; /* 悬停时的背景颜色 */
  border: 1px dashed #007bff; /* 悬停时的边框样式 */
  cursor: pointer; /* 鼠标指针样式 */
}

.cash-amount-container {
  display: flex;
  justify-content: center; /* 水平居中 */
  align-items: center; /* 垂直居中 */
  margin-top: 15px;
  padding: 10px 20px;
  background-color: #f7faff; /* 背景浅蓝色 */
  border: 1px solid #d1e3f8; /* 边框浅蓝色 */
  border-radius: 5px; /* 圆角边框 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* 轻微阴影 */
}

.cash-amount-text {
  font-size: 16px; /* 字体大小 */
  font-weight: 500; /* 字重 */
  color: #333333; /* 字体颜色 */
  margin: 0;
}

.cash-amount-value {
  font-size: 18px; /* 金额字体稍大 */
  font-weight: bold; /* 加粗金额 */
  color: #ff5722; /* 高亮橙色 */
  margin-left: 5px;
}

.center-text {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
